import {
  Earning_Mechanism_Product_Counting_Attribute,
  EarningMechanismFragment,
  Offer_Engagement_Type,
  OfferFragment,
} from '@occc/graphql';
import {
  earningMechanismHelper,
  isHiddenUntilProgressEnabled,
  isStayVisibleWhileInProgressEnabled,
} from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { OfferSubtype, OfferType } from '@occc-app/offerValidationService/types';
import { routeCreator as productGroupRouteCreator } from '@occc-app/routes/ProductGroup/utils';
import { SCAN_DATA_PLACEMENT_SELECT_ITEMS } from '@occc-app/routes/CampaignComponents/components/ScanDataConfiguration/constants';

import { InfoField } from '@occc/ui-design-system';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { formatMoneyAmount } from '@occc-app/lib/currency';

import {
  getEarningMechanismDescription,
  getAwardDescription,
  getVoucherAwardCount,
  getProductGroups,
  getCountProductsBy,
  getRounding,
  getPurchaseThreshold,
  isVarietyDiscount,
  isVarietyPurchase,
} from './configuration-section.utils';

import { getVarietyDiscountBlocks, getVarietyPurchaseBlocks } from './components';
import { SectionCard, ItemsOrViewAll } from '../../components';
import { arrangeNodeGroups, getCountingAttributeLabel, renderNodeGroups } from '../utils';
import { formatEnum, formatYesNoValues, formatYesValues } from '../formatters';
import { BLOCKS_IN_ROW } from '../constants';

type ConfigurationSectionProps = {
  earningMechanism?: EarningMechanismFragment | null;
  engagementType?: Offer_Engagement_Type | null;
  offerType?: OfferType | null;
  offerSubType?: OfferSubtype | null;
  visibleForCommandCenterFulfillment: boolean;
  shortPromptAtThePump?: string | null;
  longPromptAtThePump?: string | null;
  customData: OfferFragment['customData'];
  scanDataConfiguration?: OfferFragment['scanDataConfiguration'];
};

export const ConfigurationSection = ({
  earningMechanism,
  engagementType,
  offerType,
  offerSubType,
  visibleForCommandCenterFulfillment,
  shortPromptAtThePump,
  longPromptAtThePump,
  customData,
  scanDataConfiguration,
}: ConfigurationSectionProps) => {
  const earningMechanismType = getEarningMechanismDescription(earningMechanism);
  const awardDescription = getAwardDescription(
    earningMechanism,
    engagementType,
    offerType,
    offerSubType,
  );
  const voucherAwardCount = getVoucherAwardCount(earningMechanism);
  const countProductsBy = getCountProductsBy(earningMechanism);
  const purchaseThreshold = getPurchaseThreshold(earningMechanism);
  const rounding = getRounding(earningMechanism);

  const isMultiPurchaseConfig =
    isVarietyPurchase(earningMechanism) || isVarietyDiscount(earningMechanism);

  const { eligible = null, ineligible = null } = isMultiPurchaseConfig
    ? {}
    : getProductGroups(earningMechanism);

  const eligibleProductGroups = eligible
    ? eligible.map((pg) => ({
        id: pg.id,
        label: pg.name,
        href: productGroupRouteCreator.details(pg.id),
      }))
    : null;

  const ineligibleProductGroups = ineligible
    ? ineligible.map((pg) => ({
        id: pg.id,
        label: pg.name,
        href: productGroupRouteCreator.details(pg.id),
      }))
    : null;

  const hasPromptAtThePump = shortPromptAtThePump || longPromptAtThePump;
  const hasProductDiscount = earningMechanismHelper.isProductDiscount(earningMechanism);

  const moneyDiscountAward =
    hasProductDiscount && awardHelper.isMoneyDiscountAward(earningMechanism.productDiscountAward)
      ? earningMechanism.productDiscountAward
      : null;

  const pointsPerAward =
    earningMechanismHelper.isTotalEligiblePurchase(earningMechanism) &&
    awardHelper.isPointsPerAward(earningMechanism.totalEligiblePurchaseAward)
      ? earningMechanism.totalEligiblePurchaseAward
      : null;

  // Scan Data is only configurable in OfferConfigureForm for non-ExternalFulfillment loyalty Offers.
  const showScanDataConfiguration =
    offerType === OfferType.Offer && offerSubType !== OfferSubtype.ExternalFulfillment;
  const scanDataPlacementLabel = scanDataConfiguration
    ? SCAN_DATA_PLACEMENT_SELECT_ITEMS.find(
        (item) => item.value === scanDataConfiguration.fundingSource,
      )?.label ?? String(scanDataConfiguration.fundingSource)
    : undefined;

  const blockGroups = arrangeNodeGroups(
    [
      <InfoField label='Earning Mechanism' value={earningMechanismType} />,
      <InfoField label='Engagement Type' value={engagementType} />,
      <InfoField label='Give Members' value={awardDescription} />,
      voucherAwardCount != null && <InfoField label='Number of Awards' value={voucherAwardCount} />,

      earningMechanismHelper.isTransactionDiscount(earningMechanism) && (
        <InfoField
          label='Allow Repeat Fulfillments'
          value={formatYesNoValues([
            earningMechanism.allowMultipleFulfillmentsPerProductGroupThreshold,
          ])}
        />
      ),

      moneyDiscountAward && (
        <InfoField
          label='Partial Discount Allowed'
          value={formatYesNoValues([moneyDiscountAward?.partialDiscount])}
        />
      ),

      moneyDiscountAward && earningMechanismHelper.isProductDiscount(earningMechanism) && (
        <InfoField
          label='Allow Carry Over'
          value={formatYesNoValues([earningMechanism.allowCarryOver])}
        />
      ),

      purchaseThreshold && (
        <InfoField
          label='Purchase Threshold'
          value={
            purchaseThreshold &&
            countProductsBy === Earning_Mechanism_Product_Counting_Attribute.MoneyAmount
              ? formatMoneyAmount(purchaseThreshold)
              : purchaseThreshold
          }
        />
      ),

      pointsPerAward && (
        <InfoField label='Point Rounding' value={formatEnum(pointsPerAward.rounding)} />
      ),

      countProductsBy && (
        <InfoField
          label='Count Products By'
          value={countProductsBy && getCountingAttributeLabel(countProductsBy)}
        />
      ),

      rounding.transaction && (
        <InfoField label='Transaction Rounding' value={rounding.transaction} />
      ),

      rounding.products && <InfoField label='Round Counted Products' value={rounding.products} />,

      earningMechanismHelper.isPointSpend(earningMechanism) && (
        <InfoField label='Point Cost' value={`${earningMechanism.pointCost} Points`} />
      ),

      hasPromptAtThePump && [
        <InfoField label='Short prompt at the Pump' value={shortPromptAtThePump} />,
        <InfoField label='Long prompt at the Pump' value={longPromptAtThePump} />,
      ],

      eligibleProductGroups && (
        <ItemsOrViewAll
          label='Included Product Groups'
          itemName='Product Group'
          items={eligibleProductGroups}
        />
      ),

      ineligibleProductGroups && (
        <ItemsOrViewAll
          label='Excluded Product Groups'
          itemName='Product Group'
          items={ineligibleProductGroups}
        />
      ),

      earningMechanismHelper.isLoyaltyEvent(earningMechanism) && [
        <InfoField label='Event Type' value={earningMechanism.loyaltyEventEventType} />,
        <InfoField
          label='Visible for Command Center Fulfillment'
          value={formatYesNoValues([visibleForCommandCenterFulfillment])}
        />,
      ],

      isStayVisibleWhileInProgressEnabled(earningMechanism) && (
        <InfoField
          label='Remain Visible While In Progress'
          value={formatYesNoValues([earningMechanism.stayVisibleWhileInProgress])}
        />
      ),

      isHiddenUntilProgressEnabled(earningMechanism) && (
        <InfoField
          label='Hidden Until in Progress'
          value={formatYesNoValues([earningMechanism.hiddenUntilProgress])}
        />
      ),

      earningMechanismHelper.isPointSpend(earningMechanism) && [
        <InfoField
          label='Allowed For New Members'
          value={formatYesNoValues([earningMechanism.allowedForNewMember])}
        />,
        <InfoField
          label='Offer is Returnable'
          value={formatYesNoValues([earningMechanism.offerIsReturnable])}
        />,
      ],

      !earningMechanismHelper.isExternalFulfillment(earningMechanism) && (
        <InfoField
          label='Custom Data'
          value={
            customData && Object.keys(customData).length > 0
              ? formatYesValues([customData])
              : undefined
          }
        />
      ),

      showScanDataConfiguration && [
        <InfoField
          label='Scan Data Configuration'
          value={formatYesNoValues([!!scanDataConfiguration])}
        />,
        <InfoField label='Scan Data Placement' value={scanDataPlacementLabel} />,
        <InfoField label='Scan Data Description' value={scanDataConfiguration?.description} />,
        <InfoField
          label='Discount Allocation'
          value={
            scanDataConfiguration
              ? formatMoneyAmount(scanDataConfiguration.discountAllocation, { fromCents: true })
              : undefined
          }
        />,
      ],

      getVarietyPurchaseBlocks(earningMechanism, offerType),
      getVarietyDiscountBlocks(earningMechanism),
    ],
    BLOCKS_IN_ROW,
  );

  return (
    <SectionCard title='Configuration'>{renderNodeGroups(blockGroups, BLOCKS_IN_ROW)}</SectionCard>
  );
};
